import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Message, ConfirmationService, SelectItem } from 'primeng/api';
import { saveAs } from 'file-saver';
// https://stackoverflow.com/questions/34660265/importing-lodash-into-angular2-typescript-application
import * as _ from 'lodash';

import { TrainingSessionService, ResourceDeploymentState, TrainingStudent, Computer, TrainingSession, TrainingCloudActivityService } from '../../services/GI.Web.Services.g';
import { FileService } from '../../services/file.service';
import { ValidationService } from '../../services/validation.service';
import { RdpService } from '../../services/rdp.service';
// import { TrainingSession, TrainingStudent, ResourceDeploymentState, Computer, EvaluationStates } from '../../models/models';
import { IsAdmin } from '../../guards/isAdmin';
import { environment } from '../../../environments/environment';
import { ResourceDeploymentStateToStringPipe } from '../../pipes/resourceDeploymentStatePipe';
import { EvaluationStates } from '../../models/evaluationState';
import { ErrorHandling } from '../../ErrorHandling';

// TODO: Remove ['cloudState'] hack and remove magic strings for computer state

/** Primeng column description */
export interface IPrimeColumnInfo {
  field?: string;
  header?: string;
  width?: string;
  sortable?: boolean;
  inGlobalFilter?: boolean;
}

export interface TrainingSessionDetailColumn extends IPrimeColumnInfo {
  onlyCloudEnabled?: boolean;
}

@Component({
  selector: 'app-training-session-detail',
  templateUrl: './training-session-detail.component.html',
  styleUrls: ['./training-session-detail.component.css']
})
export class TrainingSessionDetailComponent implements OnInit {

  constructor(
    private trainingSessionService: TrainingSessionService,
    private trainingCloudActivityService: TrainingCloudActivityService,
    private fileService: FileService,
    private route: ActivatedRoute,
    private router: Router,
    private confirmationService: ConfirmationService,
    private validationService: ValidationService,
    private rdpService: RdpService,
    private resourceDeploymentStateToStringPipe: ResourceDeploymentStateToStringPipe,
    private adminGuard: IsAdmin
  ) { }

  public trainingSessionId: string;
  public trainingSession: TrainingSession;
  public trainingSessionReady = false;
  public paradiseVersions: SelectItem[] = [];
  public paradiseVersionsReady = false;
  public messages: Message[] = [];
  public isNew = false;
  public evaluationStates: any = EvaluationStates;
  public showSpinner = false;
  public preventChanges = false;
  public deploymentStateOptions = ResourceDeploymentState;
  public deploymentState: ResourceDeploymentState = ResourceDeploymentState.Unknown;
  public enableRefreshing = false;
  public enableComputerRefreshing = false;
  public passwordInvalid = false;

  public get isDeployed(): boolean {
    return this.deploymentState === ResourceDeploymentState.Succeeded;
  }

  public get canStartDeployment(): boolean {
    const deploymentState = this.deploymentState;
    return !this.isNew && (
      deploymentState === ResourceDeploymentState.Unknown ||
      deploymentState === ResourceDeploymentState.Failed ||
      deploymentState === ResourceDeploymentState.NotSpecified ||
      deploymentState === ResourceDeploymentState.Deleted);
  }

  private _deploymentRequested = false;
  public get disableDeployment(): boolean {
    const disabledStates = [
      ResourceDeploymentState.Accepted,
      ResourceDeploymentState.Created,
      ResourceDeploymentState.Creating,
      ResourceDeploymentState.NotSpecified,
      ResourceDeploymentState.Registering,
      ResourceDeploymentState.Running,
      ResourceDeploymentState.Deleting,
    ];
    return this._deploymentRequested || _(disabledStates).includes(this.deploymentState);
  }


  public get showDeleteResources(): boolean {
    return this.resourcesReady ||
      // Show button while deleting though it will be disabled
      this.deploymentState === ResourceDeploymentState.Deleting ||
      // Allow to delete resources that failed deployment so can re-try
      this.deploymentState === ResourceDeploymentState.Failed;
  }

  public get resourcesReady(): boolean {
    return this.deploymentState === ResourceDeploymentState.Succeeded;
  }

  public get deploymentName(): string {
    return this.trainingSession.cloudDeploymentName;
  }

  public get costEstimate(): string {
    if (this.trainingSession.trainingStudents.length === 0) {
      return 'add students for estimate';
    }
    const cost = this.trainingSession.trainingStudents.length * 1.35;
    return `$1.35/hr x ${this.trainingSession.trainingStudents.length} computers = $${cost.toFixed(2)}/hr`;
  }

  public get showStartAll(): boolean {
    return this.readyAndHasCloudState('Deallocated');
  }

  public get showStopAll(): boolean {
    return this.readyAndHasCloudState('Running');
  }

  private readyAndHasCloudState(state: string): boolean {
    return this.resourcesReady &&
      _.some(this.trainingSession.trainingStudents, (s: TrainingStudent) => s['cloudState'] === state);
  }

  /** Returns null or 'disabled' so can be used with [attr.disabled] */
  public get attrDisabled(): string {
    return this.isNew ? null : 'disabled';
  }

  ngOnInit() {
    this.adminGuard.canActivate();
    this.getTrainingSessionIdFromRoute();
  }

  getTrainingSessionIdFromRoute() {
    this.route.params.subscribe(params => {
      this.trainingSessionId = params['id'];
      if (!this.trainingSessionId || this.trainingSessionId === 'new') {
        this.isNew = true;
        this.createTrainingSession();
      }
      else {
        this.showSpinner = true;
        this.getTrainingSession(this.trainingSessionId);
      }
    });
  }

  createTrainingSession() {
    this.trainingSession = new TrainingSession();
    // HACK: Init training students to empty array, default credentials
    this.trainingSession.trainingStudents = [];
    this.trainingSession.cloudResourcesUserName = environment.defaultVmUserName;
    this.trainingSession.cloudResourcesPasswordPrefix = environment.defaultVmPassword;
    this.trainingSessionReady = true;
  }

  async getTrainingSession(id: string) {
    try {
      this.trainingSession = await this.trainingSessionService.getById(id).toPromise();
      this.trainingSessionReady = true;
      if (this.trainingSession.enableCloudResources) {
        this.getParadiseVersions();
        this.enableRefreshing = true;
        this.refreshDeploymentStatus();
      }
    } catch (error) {
      this.messages = ErrorHandling.getErrorMessages(error);
    }
    this.showSpinner = false;
  }

  public static get columnTitles() {
    return {
      number: 'Number',
      name: 'Name',
      email: 'Email',
      cloudStatus: 'Cloud Status',
      restart: 'Restart',
      sendEmail: 'Send Email',
      remove: 'Remove',
      stopStart: 'Stop/Start',
      connect: 'Connect',
      studentPage: 'Student Connect'
    };
  }

  // Instance member for convenience using in view
  public get columnTitles() {
    return TrainingSessionDetailComponent.columnTitles;
  }

  public get cols(): TrainingSessionDetailColumn[] {
    const titles = this.columnTitles;
    let cols = [
      { field: 'number', header: titles.number, width: '70px', sortable: true, inGlobalFilter: true, onlyCloudEnabled: false },
      { field: 'name', header: titles.name, width: '150px', sortable: true, inGlobalFilter: true, onlyCloudEnabled: false },
      { field: 'email', header: titles.email, width: '150px', sortable: true, inGlobalFilter: true, onlyCloudEnabled: false },
      { field: '', header: titles.cloudStatus, width: '100px', sortable: true, inGlobalFilter: false, onlyCloudEnabled: true },
      // { field: '', header: titles.restart, width: '50px', sortable: false, inGlobalFilter: false, onlyCloudEnabled: true },
      // { field: '', header: titles.sendEmail, width: '50px', sortable: false, inGlobalFilter: false, onlyCloudEnabled: false },
    ];
    if (this.isNew) {
      cols.push({
        field: '', header: titles.remove, width: '50px', sortable: false, inGlobalFilter: false, onlyCloudEnabled: false
      });
    }
    else {
      cols.push(
        { field: '', header: titles.stopStart, width: '50px', sortable: false, inGlobalFilter: false, onlyCloudEnabled: true },
        { field: '', header: titles.connect, width: '50px', sortable: false, inGlobalFilter: false, onlyCloudEnabled: true },
        { field: '', header: titles.studentPage, width: '50px', sortable: false, inGlobalFilter: false, onlyCloudEnabled: true },
      );
    }
    // Remove cloud columns if not cloud class, or resources not ready
    if (!this.trainingSession || !this.trainingSession.enableCloudResources || !this.resourcesReady) {
      cols = cols.filter(c => !c.onlyCloudEnabled);
    }
    return cols;
  }

  public get globalFilterFields(): string[] {
    return _.filter(this.cols, c => c.inGlobalFilter).map(c => c.field);
  }

  /** Given a column title, returns truthy for columns that should be shown in the UI */
  public showColumn(title: string): boolean {
    return !!_.find(this.cols, col => col.header === title);
  }

  enableCloudResourcesChanged(event: any) {
    if (this.trainingSession.enableCloudResources) {
      if (this.paradiseVersions == null || this.paradiseVersions.length === 0) {
        // this.trainingSession.cloudParadiseVersion = environment.defaultParadiseVersion;
        // this.trainingSession.cloudVMSize = environment.defaultTrainingVmSize;
        this.getParadiseVersions();
      }
      // We don't currently support deploying until after the class is created, so this doesn't make sense:
      // this.enableRefreshing = true;
      // this.refreshDeploymentStatus();
    }
    else {
      this.enableRefreshing = false;
    }
  }

  async getDeploymentStatus(): Promise<ResourceDeploymentState> {
    // tslint:disable-next-line:curly
    if (!this.trainingSession)
      return ResourceDeploymentState.Unknown;
    try {
      return await this.trainingCloudActivityService.getDeploymentState(this.trainingSessionId).toPromise();
    } catch (error) {
      ErrorHandling.getErrorMessages(error, 'Could not get Deployment State:');
    }
    return ResourceDeploymentState.Unknown;
  }

  async refreshDeploymentStatus() {
    do {
      const status = await this.getDeploymentStatus();
      this.updateDeploymentState(status);
      await this.getStudentComputerStatuses();
      await this.delay(environment.pollTime);
    } while (this.enableRefreshing);
  }

  async delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async refreshComputerStatuses(): Promise<void> {
    do {
      await this.getStudentComputerStatuses();
      await this.delay(environment.pollTime);
    } while (this.enableComputerRefreshing);
  }

  // TODO: Separate UI for showing the Deployment state.  Using this.messages stomps on error message returned from fetching computer status.
  updateDeploymentState(newState: ResourceDeploymentState) {
    if (this.deploymentState !== newState) {
      this.messages = [{
        severity: 'info',
        summary: 'Deployment Update:',
        detail: this.resourceDeploymentStateToStringPipe.transform(newState)
      }];
    }

    this.deploymentState = newState;
    if (this.deploymentState === ResourceDeploymentState.Deleted ||
      this.deploymentState === ResourceDeploymentState.Failed ||
      this.deploymentState === ResourceDeploymentState.Canceled) {
      this.enableRefreshing = false;
    }
  }

  async getStudentComputerStatuses(): Promise<Computer[]> {
    // If the deployment has not succeeded, we should not be checking the computers (connecting to failed computers not supported right now)
    if (!this.isDeployed) {
      return [];
    }
    try {
      const computers = await this.trainingCloudActivityService.getTrainingComputers(this.trainingSessionId).toPromise();
      // O(N^2)
      computers.forEach(computer => {
        const studentToUpdate = this.trainingSession.trainingStudents.find(s => s.number === computer.computerNumber);
        if (studentToUpdate) {
          studentToUpdate['cloudState'] = computer.state;
        }
      });
      return computers;
    } catch (error) {
      ErrorHandling.getErrorMessages(error, 'Could not get Student Machine State:');
    }
    return [];
  }

  updateInstructor() {
    const name = this.trainingSession.instructor || 'Instructor Not Defined';
    if (this.trainingSession.trainingStudents.length) {
      this.trainingSession.trainingStudents[0].name = name;
    }
    else {
      this.trainingSession.trainingStudents[0] = new TrainingStudent({ number: '0', name: name });
    }
  }

  addStudent() {
    this.updateInstructor();
    const studentNumber = this.trainingSession.trainingStudents.length.toString();
    this.trainingSession.trainingStudents.push(new TrainingStudent({ number: studentNumber }));
  }

  public async startStudent(student: TrainingStudent) {
    try {
      await this.trainingCloudActivityService.startComputer(this.trainingSessionId, student.number).toPromise();
    } catch (error) {
      this.messages = ErrorHandling.getErrorMessages(error, 'Could not start VM:');
    }
  }

  public async stopStudent(student: TrainingStudent) {
    try {
      await this.trainingCloudActivityService.stopComputer(this.trainingSessionId, student.number).toPromise();
    } catch (error) {
      this.messages = ErrorHandling.getErrorMessages(error, 'Could not stop VM:');
    }
  }

  public async restartStudent(student: TrainingStudent) {
    try {
      await this.trainingCloudActivityService.restartComputer(this.trainingSessionId, student.number).toPromise();
    } catch (error) {
      this.messages = ErrorHandling.getErrorMessages(error, 'Could not restart VM:');
    }
  }

  confirmSendStudentEmail(student: TrainingStudent) {
    this.confirmationService.confirm({
      message: 'Are you sure you want to EMAIL student: ' + student.name + '?',
      accept: () => {
        this.emailStudent(student);
      }
    });
  }

  confirmRemove(student: TrainingStudent) {
    this.confirmationService.confirm({
      message: 'Are you sure you want to REMOVE student: ' + (student.name || student.number) + '?',
      accept: () => {
        this.removeStudent(student);
      }
    });
  }

  emailStudent(student: TrainingStudent) {

  }

  removeStudent(student: TrainingStudent) {
    _.remove(this.trainingSession.trainingStudents, student);
  }

  async create() {
    if (!this.preventChanges) {
      this.preventChanges = true;
      this.showSpinner = true;
      try {
        this.trainingSession = await this.trainingSessionService.post(this.trainingSession).toPromise();
        this.router.navigate(['/trainingsessions']);
      } catch (error) {
        this.messages = ErrorHandling.getErrorMessages(error);
      }
      this.preventChanges = this.showSpinner = false;
    }
  }

  async update() {
    this.showSpinner = true;
    try {
      await this.trainingSessionService.put(this.trainingSessionId, <TrainingSession>this.trainingSession).toPromise();
      this.router.navigate(['/trainingsessions']);
    } catch (error) {
      this.messages = ErrorHandling.getErrorMessages(error, 'Could not update Training Session:');
    }
    this.showSpinner = false;
  }

  async deploy() {
    this._deploymentRequested = true;
    this.messages = [{
      severity: 'info',
      summary: 'Starting Training Resources Deployment...'
    }];
    try {
      // Make sure cloud settings have been updated to database before calling start
      this.trainingSession = await this.trainingSessionService.put(this.trainingSessionId, this.trainingSession).toPromise();
      await this.trainingCloudActivityService.deploy(this.trainingSessionId).toPromise();
      this.enableRefreshing = true;
      this.refreshDeploymentStatus();
    } catch (error) {
      this.messages = ErrorHandling.getErrorMessages(error, 'Could not Start deploying Cloud Resources:');
    }
    this._deploymentRequested = false;
  }

  deleteResources() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to PERMANENTLY DELETE all virtual machines for this session?',
      accept: () => this.deleteResourcesConfirmed()
    });
  }

  async deleteResourcesConfirmed() {
    this.messages = [{
      severity: 'info',
      summary: 'Deleting Cloud Resources...'
    }];
    try {
      await this.trainingCloudActivityService.deleteResources(this.trainingSessionId).toPromise();
      this.messages = [];
      if (!this.enableRefreshing) {
        this.enableRefreshing = true;
        this.refreshDeploymentStatus();
      }
    } catch (error) {
      this.messages = ErrorHandling.getErrorMessages(error, 'Could not destroy Cloud Resources:');
    }
  }

  async startAllComputers(): Promise<void> {
    this.enableComputerRefreshing = true;
    this.refreshComputerStatuses();
    try {
      await this.trainingCloudActivityService.startAllComputers(this.trainingSessionId).toPromise();
      this.refreshComputerStatuses();
    } catch (error) {
      this.messages = ErrorHandling.getErrorMessages(error, 'Failed to start all computers:');
    }
    this.enableComputerRefreshing = false;
  }

  async stopAllComputers(): Promise<void> {
    this.enableComputerRefreshing = true;
    this.refreshComputerStatuses();
    try {
      await this.trainingCloudActivityService.stopAllComputers(this.trainingSessionId).toPromise();
      this.refreshComputerStatuses();
    } catch (error) {
      this.messages = ErrorHandling.getErrorMessages(error, 'Failed to stop all computers:');
    }
    this.enableComputerRefreshing = false;
  }

  cancel() {
    // Try to kill any refreshing
    this.enableRefreshing = false;
    this.enableComputerRefreshing = false;
    this.router.navigate(['/trainingsessions']);
  }

  // TODO: This code was copied from somewhere?
  getParadiseVersions() {
    this.fileService.getFilesFull(true, true, true, null).subscribe(results => {
      if (results) {
        const versions = results[1];
        versions.forEach(version => {
          if (version.name.includes('ParadiseSetup')) {
            const beginningIndex = ('Paradise/').length;
            const endingIndex = version.name.indexOf('/', beginningIndex);
            if (version.beta) {
              this.paradiseVersions.push({
                label: version.name.substring(beginningIndex, endingIndex) + ' (beta)',
                value: version.name.substring(beginningIndex, endingIndex)
              });
            }
            else if (version.archive) {
              this.paradiseVersions.push({
                label: version.name.substring(beginningIndex, endingIndex) + ' (archived)',
                value: version.name.substring(beginningIndex, endingIndex)
              });
            }
            else {
              this.paradiseVersions.push({
                label: version.name.substring(beginningIndex, endingIndex),
                value: version.name.substring(beginningIndex, endingIndex)
              });
            }

            // set default version based on current release
            if (version.release && !version.archive && !version.beta && !this.trainingSession.cloudParadiseVersion) {
              this.trainingSession.cloudParadiseVersion = version.name.substring(beginningIndex, endingIndex);
            }
          }

        });
        this.paradiseVersions.sort((a, b) => {
          const aValue = this.calculateVersionValue(a.label);
          const bValue = this.calculateVersionValue(b.label);
          if (aValue > bValue) {
            return 1;
          }
          if (aValue < bValue) {
            return -1;
          }
          return 0;
        });
      }
      this.paradiseVersionsReady = true;
    });
  }

  calculateVersionValue(version: string): number {
    const firstPeriodIndex = version.indexOf('.');
    const secondPeriodIndex = version.lastIndexOf('.');
    const hundredThousands = parseInt(version.substring(0, firstPeriodIndex), 10) * 100000;
    const thousands = parseInt(version.substring(firstPeriodIndex + 1, secondPeriodIndex), 10) * 10000;
    const ones = parseInt(version.substring(secondPeriodIndex + 1), 10);
    return hundredThousands + thousands + ones;
  }

  validatePassword() {
    this.passwordInvalid = !(this.validationService.validatePassword(this.trainingSession.cloudResourcesPasswordPrefix));
  }

  downloadRdp(student: TrainingStudent) {
    if (this.trainingSession.enableCloudResources && this.deploymentName) {
      this.trainingCloudActivityService.getTrainingComputers(this.trainingSessionId)
        .subscribe(
          results => {
            const targetComputer = results.find(r => r.computerNumber === student.number);
            this.buildRdpFile(targetComputer);
          }
        );
    }
  }

  buildRdpFile(computer: Computer) {
    if (computer && computer.ipAddress) {
      const rdpString = this.rdpService.buildRdpFileAsString(computer, this.trainingSession.cloudResourcesUserName);
      const blob = new Blob([rdpString], { type: 'text/plain;charset=utf-8' });
      saveAs(blob, this.rdpService.setRdpFileName(computer));
    }
    else {
      this.messages = [];
      this.messages.push({
        severity: 'error',
        summary: 'Cannot download RDP File.',
        detail: 'VM is not ready or information is not known.'
      });
    }
  }

  navigateToStudentConnect(student: TrainingStudent) {
    // Try to kill any refreshing
    this.enableRefreshing = false;
    this.enableComputerRefreshing = false;
    this.router.navigate(['/usertraining/' + encodeURIComponent(student.studentId)]);
  }
}



// WEBPACK FOOTER //
// ./src/app/components/training-session-detail/training-session-detail.component.ts